import React, { useEffect, useState } from "react";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DemocracyWorksElections from "../../components/UpcomingElectionsComponents/DemocracyWorksElections";
import {
  canonicalSlugForLegacy,
  relatedStates,
  stateBySlug,
} from "../../utils/usStates";
import {
  fetchPublicDWStateAuthority,
  fetchPublicDWStateElections,
} from "../../utils/democracyWorksApi";
import generatedAuthorities from "../../data/dwStateAuthorities.generated.json";
import generatedElections from "../../data/dwStateElections.generated.json";

const SITE_URL = "https://civixthread.com";
const APP_STORE_URL = "https://apps.apple.com/us/app/civixthread/id6756985264";

const PRIMARY = "#047857";
const PRIMARY_DARK = "#065f46";
const TEXT_PRIMARY = "#0f172a";
const TEXT_SECONDARY = "#334155";

function sanitizeHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/ on[a-z]+="[^"]*"/gi, "")
    .replace(/ on[a-z]+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}

function htmlToPlainText(html) {
  if (!html) return "";
  return html
    .replace(/<\/p>|<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function pickQuestionAnswers(authority) {
  if (!authority) return [];
  const list =
    authority.questions ||
    authority.questionsAndAnswers ||
    authority.q_and_a ||
    [];
  if (!Array.isArray(list)) return [];
  return list
    .map((entry) => {
      const q = entry.question || entry.q || entry.title || "";
      const a = entry.answer || entry.a || entry.text || entry.body || "";
      return { question: q.trim(), answerHtml: a, answerText: htmlToPlainText(a) };
    })
    .filter((x) => x.question && x.answerText);
}

// Democracy Works returns address fields as either a plain string OR a
// structured object like { street, street2, city, state, zip }. Coerce both
// shapes into a single displayable string so React never receives an object
// as a child (which throws "Objects are not valid as a React child").
function formatAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (typeof addr !== "object") return String(addr);
  const street = addr.street || addr.line1 || addr.streetAddress || "";
  const street2 = addr.street2 || addr.line2 || addr.suite || "";
  const city = addr.city || addr.locationName || "";
  const state = addr.state || addr.region || addr.stateCode || "";
  const zip = addr.zip || addr.zipCode || addr.postalCode || "";
  const cityStateZip = [city, [state, zip].filter(Boolean).join(" ").trim()]
    .filter(Boolean)
    .join(", ");
  return [street, street2, cityStateZip].filter(Boolean).join(", ");
}

// Defensive scalar coercion. DW occasionally wraps phone/email/website in
// objects too — falling back to a string keeps React happy in every case.
function asScalarString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    return (
      value.value ||
      value.url ||
      value.address ||
      value.text ||
      value.number ||
      ""
    );
  }
  return "";
}

function pickContact(authority) {
  if (!authority) return null;
  const contact = authority.contact || authority.contactInfo || {};
  const phone = asScalarString(contact.phone || authority.phone);
  const email = asScalarString(contact.email || authority.email);
  const website = asScalarString(
    authority.website || contact.website || authority.officialWebsite
  );
  const address = formatAddress(
    contact.physicalAddress || contact.address || authority.physicalAddress
  );
  const mailing = formatAddress(
    contact.mailingAddress || authority.mailingAddress
  );
  if (!phone && !email && !website && !address && !mailing) return null;
  return { phone, email, website, address, mailing };
}

// DW returns method support flags as `supported` (the documented field) but
// some legacy paths use `available`. Coalesce both so the lead paragraph and
// Key Facts block render correctly regardless of which shape we receive.
function isMethodSupported(methodObj) {
  if (!methodObj || typeof methodObj !== "object") return false;
  return Boolean(methodObj.supported || methodObj.available);
}

function buildLeadParagraph(stateName, authority) {
  const methods = [];
  if (isMethodSupported(authority?.registration?.online)) methods.push("online");
  if (isMethodSupported(authority?.registration?.byMail)) methods.push("by mail");
  if (isMethodSupported(authority?.registration?.inPerson))
    methods.push("in person");
  const methodsStr = methods.length
    ? methods.join(", ")
    : "online, by mail, or in person (rules vary)";
  return `${stateName} voters can register to vote ${methodsStr}. Use this guide to find 2026 ${stateName} election dates, voter registration rules, ballot measure information, candidate information when available, and official election office links.`;
}

// Format an ISO date (YYYY-MM-DD) as "Tuesday, June 2, 2026" — long form is
// the format Google AI Overviews and Perplexity prefer to lift verbatim.
function formatLongDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  // Use UTC parts to avoid TZ shifts when prerendered in different envs.
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatShortDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

const ELECTION_TYPE_LABEL = {
  presidentialPrimary: "Presidential Primary",
  state: "State General",
  stateSenate: "State Senate",
  stateHouse: "State House",
  congressional: "Congressional",
  senate: "U.S. Senate",
  county: "County",
  subCounty: "Local (Sub-County)",
  municipal: "Municipal",
  subMunicipal: "Local (Sub-Municipal)",
  school: "School District",
  special: "Special Election",
};

function describeElection(election) {
  if (!election) return "Election";
  const desc = (election.description || "").trim();
  if (desc) return desc;
  return ELECTION_TYPE_LABEL[election.type] || "Election";
}

function isPrimary(election) {
  if (!election) return false;
  if (/primary/i.test(election.description || "")) return true;
  if (/primary/i.test(election.type || "")) return true;
  return false;
}

function isGeneral(election) {
  if (!election) return false;
  if (/general/i.test(election.description || "")) return true;
  if (election.type === "state" && !isPrimary(election)) return true;
  return false;
}

// Pull the earliest upcoming primary and general from the elections list so
// we can populate the at-a-glance Key Facts block.
function pickKeyDates(elections) {
  const sorted = (elections || [])
    .filter((e) => e?.date && /^\d{4}-\d{2}-\d{2}$/.test(e.date))
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));
  return {
    primary: sorted.find(isPrimary) || null,
    general: sorted.find(isGeneral) || null,
    next: sorted[0] || null,
  };
}

function buildKeyFacts({ stateName, authority, elections }) {
  const { primary, general, next } = pickKeyDates(elections);

  const facts = [];

  if (primary) {
    facts.push({
      label: "2026 primary election",
      valueIso: primary.date,
      value: formatLongDate(primary.date),
    });
  }
  if (general) {
    facts.push({
      label: "2026 general election",
      valueIso: general.date,
      value: formatLongDate(general.date),
    });
  } else if (!primary && next) {
    facts.push({
      label: `Next ${stateName} election`,
      valueIso: next.date,
      value: `${formatLongDate(next.date)}, ${describeElection(next)}`,
    });
  }

  // Voter registration methods
  const regMethods = [];
  if (isMethodSupported(authority?.registration?.online)) regMethods.push("online");
  if (isMethodSupported(authority?.registration?.byMail)) regMethods.push("by mail");
  if (isMethodSupported(authority?.registration?.inPerson))
    regMethods.push("in person");
  if (regMethods.length) {
    facts.push({
      label: "Register to vote",
      value: regMethods.join(", "),
    });
  }

  // Same-day registration
  const sameDay =
    authority?.registration?.inPerson?.atPollingPlaceOnElectionDay === true ||
    /can register to vote on election day/i.test(
      authority?.questionAndAnswer?.electionDayRegistration?.answer || ""
    );
  if (typeof sameDay === "boolean") {
    facts.push({
      label: "Same-day registration",
      value: sameDay ? "Yes" : "No",
    });
  }

  // Vote by mail
  const byMailAuto = authority?.voting?.mailBallotsSentAutomatically === true;
  const byMailExcuse = authority?.voting?.byMail?.excuseRequired;
  if (byMailAuto) {
    facts.push({
      label: "Vote by mail",
      value: "Mail ballots sent automatically to all active voters",
    });
  } else if (byMailExcuse === true) {
    facts.push({
      label: "Vote by mail",
      value: "Available with an approved excuse",
    });
  } else if (byMailExcuse === false) {
    facts.push({
      label: "Vote by mail",
      value: "Available to any registered voter (no excuse required)",
    });
  }

  // Photo ID at the polls
  const idRequired = authority?.voting?.inPerson?.idRequiredAllVoters;
  if (idRequired === true) {
    facts.push({ label: "Photo ID at the polls", value: "Required for all voters" });
  } else if (idRequired === false) {
    facts.push({ label: "Photo ID at the polls", value: "Not required for all voters" });
  }

  // Early voting
  const earlySupported = authority?.voting?.early?.supported;
  if (earlySupported === true) {
    facts.push({ label: "Early in-person voting", value: "Available" });
  } else if (earlySupported === false) {
    facts.push({ label: "Early in-person voting", value: "Not offered statewide" });
  }

  return facts;
}

// Build the registration intro paragraph using only the methods Democracy
// Works actually marks as supported for this state. Falls back to a
// deliberately generic, true-for-all-51-jurisdictions sentence when DW data
// is missing so we never assert a method that isn't actually offered.
function buildRegistrationIntro(stateName, authority) {
  const methods = [];
  if (isMethodSupported(authority?.registration?.online)) methods.push("online");
  if (isMethodSupported(authority?.registration?.byMail)) methods.push("by mail");
  if (isMethodSupported(authority?.registration?.inPerson))
    methods.push("in person at your local election office");
  if (methods.length === 0) {
    return `${stateName} accepts voter registration through one or more methods. Specific eligibility, ID requirements, and deadlines vary by state. See the official ${stateName} election office below for the latest rules.`;
  }
  let methodsStr;
  if (methods.length === 1) methodsStr = methods[0];
  else if (methods.length === 2) methodsStr = `${methods[0]} or ${methods[1]}`;
  else methodsStr = `${methods.slice(0, -1).join(", ")}, or ${methods[methods.length - 1]}`;
  return `${stateName} accepts voter registration ${methodsStr}. Specific eligibility, ID requirements, and deadlines vary. See the official ${stateName} election office below for the latest rules.`;
}

function pickAnswer(authority, key) {
  const qa = authority?.questionAndAnswer || {};
  const entry = qa[key];
  if (!entry) return "";
  return htmlToPlainText(entry.answer || "").trim();
}

const KeyFactsCard = ({ stateName, generatedAt, facts }) => {
  if (!facts || facts.length === 0) return null;
  const asOf = generatedAt
    ? new Date(generatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  return (
    <Box
      component="section"
      aria-labelledby="key-facts-heading"
      sx={{
        mb: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        backgroundColor: "#f0fdf4",
        border: `1px solid ${PRIMARY}33`,
      }}
    >
      <Typography
        component="h2"
        id="key-facts-heading"
        sx={{
          fontSize: "1.05rem",
          fontWeight: 800,
          color: TEXT_PRIMARY,
          mb: 1,
        }}
      >
        Key facts about 2026 {stateName} elections
        {asOf && (
          <Typography
            component="span"
            sx={{
              fontSize: "0.78rem",
              fontWeight: 500,
              color: TEXT_SECONDARY,
              ml: 1,
            }}
          >
            (as of <time dateTime={generatedAt}>{asOf}</time>)
          </Typography>
        )}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
        {facts.map((fact, i) => (
          <Box
            component="li"
            key={`fact-${i}`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              py: 0.5,
              color: TEXT_PRIMARY,
            }}
          >
            <CheckCircleIcon
              aria-hidden="true"
              sx={{ fontSize: 18, color: PRIMARY_DARK, mt: 0.25, flexShrink: 0 }}
            />
            <Typography
              component="span"
              sx={{ fontSize: "0.95rem", lineHeight: 1.5 }}
            >
              <strong style={{ color: TEXT_PRIMARY }}>{fact.label}:</strong>{" "}
              {fact.valueIso ? (
                <time dateTime={fact.valueIso}>{fact.value}</time>
              ) : (
                fact.value
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ElectionDatesTable = ({ stateName, elections }) => {
  const rows = (elections || [])
    .filter((e) => e?.date && /^\d{4}-\d{2}-\d{2}$/.test(e.date))
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 10);
  if (rows.length === 0) return null;

  return (
    <Box sx={{ overflowX: "auto", mt: 1.5 }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.92rem",
          "& th, & td": {
            borderBottom: "1px solid #e2e8f0",
            padding: "10px 12px",
            textAlign: "left",
            verticalAlign: "top",
            color: TEXT_PRIMARY,
          },
          "& th": {
            backgroundColor: "#f8fafc",
            fontWeight: 700,
            fontSize: "0.85rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: TEXT_SECONDARY,
          },
        }}
      >
        <caption
          style={{
            captionSide: "bottom",
            fontSize: "0.78rem",
            color: TEXT_SECONDARY,
            paddingTop: 8,
            textAlign: "left",
          }}
        >
          Upcoming {stateName} elections. Source: Democracy Works.
        </caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Election</th>
            <th scope="col">Type</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e, i) => (
            <tr key={`row-${i}-${e.date}-${e.ocdId || ""}`}>
              <td>
                <time dateTime={e.date}>{formatShortDate(e.date)}</time>
              </td>
              <td>{describeElection(e)}</td>
              <td>{ELECTION_TYPE_LABEL[e.type] || e.type || ""}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

// Question-style H2 followed by a direct, single-paragraph answer. This is
// the highest-leverage GEO pattern for People-Also-Ask snippets and AI
// Overviews — Google and LLMs lift these verbatim.
const PeopleAlsoAskBlock = ({ items }) => (
  <Box>
    {items.map(({ question, answer }, i) => (
      <Box key={`paa-${i}`} sx={{ mb: 3 }}>
        <Typography
          component="h2"
          id={`paa-${i}`}
          sx={{
            fontSize: { xs: "1.15rem", md: "1.3rem" },
            fontWeight: 700,
            color: TEXT_PRIMARY,
            mb: 1,
            mt: 3,
          }}
        >
          {question}
        </Typography>
        <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.65 }}>
          {answer}
        </Typography>
      </Box>
    ))}
  </Box>
);

function buildPeopleAlsoAskItems({ stateName, authority, elections }) {
  const { primary, general } = pickKeyDates(elections);
  const items = [];

  if (primary) {
    items.push({
      question: `When is the 2026 primary election in ${stateName}?`,
      answer: `The 2026 ${stateName} primary election is on ${formatLongDate(
        primary.date
      )}. ${describeElection(primary)} appears on the ballot.`,
    });
  }
  if (general) {
    items.push({
      question: `When is the 2026 general election in ${stateName}?`,
      answer: `The 2026 ${stateName} general election is on ${formatLongDate(
        general.date
      )}. Federal, state, and many local offices appear on the ballot.`,
    });
  }

  // Voter ID
  const voterIdAns = pickAnswer(authority, "voterId");
  if (voterIdAns) {
    items.push({
      question: `Do I need ID to vote in ${stateName}?`,
      answer: voterIdAns.slice(0, 360),
    });
  }

  // Vote by mail
  const byMailAns = pickAnswer(authority, "voteByMail");
  if (byMailAns) {
    items.push({
      question: `How do I vote by mail in ${stateName}?`,
      answer: byMailAns.slice(0, 360),
    });
  }

  // Same-day registration
  const sameDayAns = pickAnswer(authority, "electionDayRegistration");
  if (sameDayAns) {
    items.push({
      question: `Can I register to vote on Election Day in ${stateName}?`,
      answer: sameDayAns.slice(0, 360),
    });
  }

  return items;
}

const SectionHeading = ({ children, id }) => (
  <Typography
    component="h2"
    id={id}
    sx={{
      fontSize: { xs: "1.3rem", md: "1.6rem" },
      fontWeight: 700,
      color: TEXT_PRIMARY,
      mb: 1.5,
      mt: 4,
    }}
  >
    {children}
  </Typography>
);

const FaqAccordion = ({ items }) => (
  <Box>
    {items.map(({ question, answerHtml }, idx) => (
      <Box
        key={`${idx}-${question}`}
        component="details"
        sx={{
          mb: 1.5,
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          p: 1.5,
          "& > summary": {
            cursor: "pointer",
            fontWeight: 700,
            color: TEXT_PRIMARY,
            fontSize: "1rem",
            outline: "none",
          },
          "& > summary:focus-visible": {
            outline: `2px solid ${PRIMARY}`,
            outlineOffset: 2,
          },
        }}
      >
        <Typography component="summary">{question}</Typography>
        <Box
          sx={{
            mt: 1,
            color: TEXT_SECONDARY,
            lineHeight: 1.6,
            "& a": { color: PRIMARY_DARK },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(answerHtml) }}
        />
      </Box>
    ))}
  </Box>
);

const ContactCard = ({ stateName, contact }) => (
  <Box
    component="address"
    sx={{
      mt: 2,
      p: 2.5,
      borderRadius: 3,
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      fontStyle: "normal",
      color: TEXT_PRIMARY,
    }}
  >
    <Typography sx={{ fontWeight: 700, color: TEXT_PRIMARY, mb: 1 }}>
      {stateName} election office
    </Typography>
    <Stack spacing={0.75}>
      {contact.phone && (
        <Typography sx={{ color: TEXT_SECONDARY }}>
          <strong style={{ color: TEXT_PRIMARY }}>Phone:</strong> {contact.phone}
        </Typography>
      )}
      {contact.email && (
        <Typography sx={{ color: TEXT_SECONDARY }}>
          <strong style={{ color: TEXT_PRIMARY }}>Email:</strong>{" "}
          <a
            href={`mailto:${contact.email}`}
            style={{ color: PRIMARY_DARK, fontWeight: 600 }}
          >
            {contact.email}
          </a>
        </Typography>
      )}
      {contact.address && (
        <Typography sx={{ color: TEXT_SECONDARY }}>
          <strong style={{ color: TEXT_PRIMARY }}>Address:</strong>{" "}
          {contact.address}
        </Typography>
      )}
      {contact.mailing && (
        <Typography sx={{ color: TEXT_SECONDARY }}>
          <strong style={{ color: TEXT_PRIMARY }}>Mailing:</strong>{" "}
          {contact.mailing}
        </Typography>
      )}
      {contact.website && (
        <Typography sx={{ color: TEXT_SECONDARY }}>
          <strong style={{ color: TEXT_PRIMARY }}>Official site:</strong>{" "}
          <a
            href={contact.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: PRIMARY_DARK, fontWeight: 600 }}
          >
            {contact.website}
          </a>
        </Typography>
      )}
    </Stack>
    <Typography sx={{ mt: 1.5, fontSize: "0.75rem", color: TEXT_SECONDARY }}>
      Voting information provided by Democracy Works.
    </Typography>
  </Box>
);

function buildJsonLd({
  state,
  authority,
  faqs,
  elections,
  contact,
  generatedAt,
  paaItems,
}) {
  // Build-time stamp for both datePublished and dateModified — stable across
  // crawls so Google sees genuine freshness signals (not "today" every render).
  const stamp = generatedAt || new Date().toISOString();
  const url = `${SITE_URL}/elections/${state.slug}`;
  const stateName = state.name;
  const officialUrl = contact?.website || `${SITE_URL}/elections/${state.slug}`;

  const publisher = {
    "@type": "Organization",
    name: "CivixThread",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/logo512.png`,
    },
  };

  const docs = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": url,
      url,
      name: `2026 ${stateName} Elections & Voter Guide`,
      description: `Find 2026 ${stateName} election dates, voter registration rules, ballot measures, candidates, and the official ${stateName} election office.`,
      inLanguage: "en-US",
      datePublished: stamp,
      dateModified: stamp,
      isPartOf: { "@type": "WebSite", name: "CivixThread", url: SITE_URL },
      publisher,
      about: { "@type": "AdministrativeArea", name: stateName },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["#state-lead-paragraph", "#key-facts-heading"],
      },
      potentialAction: {
        "@type": "RegisterAction",
        name: `Register to vote in ${stateName}`,
        target: officialUrl,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Elections",
          item: `${SITE_URL}/elections`,
        },
        { "@type": "ListItem", position: 3, name: stateName, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentService",
      name: `Voter Registration in ${stateName}`,
      provider: {
        "@type": "GovernmentOrganization",
        name: `${stateName} Election Office`,
        url: officialUrl,
      },
      areaServed: { "@type": "AdministrativeArea", name: stateName },
      audience: {
        "@type": "PeopleAudience",
        audienceType: `${stateName} residents`,
      },
      url,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "CivixThread",
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png`,
      sameAs: [APP_STORE_URL],
    },
  ];

  // Merge programmatic People-Also-Ask Q&A into the FAQPage schema so each
  // question becomes individually eligible for SERP rich-result treatment.
  const allFaqs = [];
  if (Array.isArray(paaItems)) {
    paaItems.forEach((p) => {
      if (p?.question && p?.answer) {
        allFaqs.push({ question: p.question, answerText: p.answer });
      }
    });
  }
  if (Array.isArray(faqs)) {
    faqs.forEach((f) => {
      if (f?.question && f?.answerText) {
        allFaqs.push({ question: f.question, answerText: f.answerText });
      }
    });
  }

  if (allFaqs.length > 0) {
    docs.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs.slice(0, 16).map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answerText },
      })),
    });
  }

  // HowTo (register to vote in this state) — synthesized from authority data.
  if (authority) {
    const steps = [];
    if (isMethodSupported(authority?.registration?.online)) {
      steps.push({
        "@type": "HowToStep",
        name: "Register online",
        text: `Register online through the ${stateName} election office.`,
        url: authority?.registration?.online?.url || officialUrl,
      });
    }
    if (isMethodSupported(authority?.registration?.byMail)) {
      steps.push({
        "@type": "HowToStep",
        name: "Register by mail",
        text: `Download, complete, and mail the ${stateName} voter registration form.`,
      });
    }
    if (isMethodSupported(authority?.registration?.inPerson)) {
      steps.push({
        "@type": "HowToStep",
        name: "Register in person",
        text: `Register in person at your local ${stateName} election office.`,
      });
    }
    if (steps.length > 0) {
      docs.push({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: `How to register to vote in ${stateName}`,
        step: steps,
      });
    }
  }

  // Event[] entries — only emit for elections with a real date.
  const upcoming = (elections || []).filter(
    (e) => e?.date && /^\d{4}-\d{2}-\d{2}$/.test(e.date)
  );
  if (upcoming.length > 0) {
    upcoming.slice(0, 10).forEach((e) => {
      docs.push({
        "@context": "https://schema.org",
        "@type": "Event",
        name: e.description || "Election Day",
        startDate: e.date,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "AdministrativeArea",
          name: stateName,
        },
        organizer: {
          "@type": "GovernmentOrganization",
          name: `${stateName} Election Office`,
          url: officialUrl,
        },
      });
    });
  }

  return docs;
}

export default function PublicStateElectionsPage() {
  const { stateSlug: rawSlug } = useParams();
  const slug = (rawSlug || "").toLowerCase();
  const state = stateBySlug(slug);
  // Two-letter legacy slugs (e.g. /elections/ca, /elections/dc) redirect to
  // the new canonical full-name slug. Done before the unknown-state check so
  // we never 404 a previously valid URL.
  const legacyTarget = !state ? canonicalSlugForLegacy(slug) : null;

  // Static evergreen + time-sensitive content from the build-time JSON files.
  // Read first so the prerendered HTML carries SEO content (and ballot data
  // for AI/LLM crawlers) before any network call. Runtime fetch refreshes
  // whatever the build snapshot might have missed.
  const seedAuthority =
    generatedAuthorities?.authorities?.[slug]?.authority || null;
  const seedElections = Array.isArray(
    generatedElections?.elections?.[slug]?.elections
  )
    ? generatedElections.elections[slug].elections
    : [];
  // Build-time timestamp for stable dateModified / "Last updated" — avoids
  // false freshness signals to Google when nothing has actually changed.
  const buildGeneratedAt =
    generatedElections?.generatedAt ||
    generatedAuthorities?.generatedAt ||
    null;

  const [authority, setAuthority] = useState(seedAuthority);
  const [elections, setElections] = useState(seedElections);
  const [hasFetchedRuntime, setHasFetchedRuntime] = useState(false);

  useEffect(() => {
    if (!state) return;
    let cancelled = false;
    const tasks = [];
    if (!seedAuthority) {
      tasks.push(
        fetchPublicDWStateAuthority(state.code).then((data) => {
          if (!cancelled && data) setAuthority(data);
        })
      );
    }
    tasks.push(
      fetchPublicDWStateElections(state.code).then((res) => {
        if (cancelled) return;
        const fresh = Array.isArray(res?.elections) ? res.elections : [];
        // Only overwrite the seeded list if we actually got fresh data back —
        // a network failure must not blank out the prerendered content.
        if (fresh.length > 0 || seedElections.length === 0) {
          setElections(fresh);
        }
      })
    );
    Promise.allSettled(tasks).then(() => {
      if (!cancelled) setHasFetchedRuntime(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, seedAuthority]);

  if (rawSlug && rawSlug !== rawSlug.toLowerCase()) {
    return <Navigate to={`/elections/${rawSlug.toLowerCase()}`} replace />;
  }

  if (legacyTarget) {
    return <Navigate to={`/elections/${legacyTarget}`} replace />;
  }

  if (!state) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: "60vh",
          py: 8,
          textAlign: "center",
          backgroundColor: "#ffffff",
        }}
      >
        <Helmet>
          <title>State not found | CivixThread</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Container maxWidth="sm">
          <Typography
            component="h1"
            sx={{ fontSize: "1.75rem", fontWeight: 800, color: TEXT_PRIMARY }}
          >
            State not found
          </Typography>
          <Typography sx={{ color: TEXT_SECONDARY, mt: 1.5, mb: 3 }}>
            We couldn't find an elections guide for that state. Browse all 50
            states plus DC on the elections hub.
          </Typography>
          <Button
            component={RouterLink}
            to="/elections"
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: PRIMARY_DARK },
            }}
          >
            Back to elections hub
          </Button>
        </Container>
      </Box>
    );
  }

  const faqs = pickQuestionAnswers(authority);
  const contact = pickContact(authority);
  const lead = buildLeadParagraph(state.name, authority);
  const registrationIntro = buildRegistrationIntro(state.name, authority);
  const keyFacts = buildKeyFacts({
    stateName: state.name,
    authority,
    elections,
  });
  const paaItems = buildPeopleAlsoAskItems({
    stateName: state.name,
    authority,
    elections,
  });
  const related = relatedStates(state.code, 8);
  const jsonLd = buildJsonLd({
    state,
    authority,
    faqs,
    elections,
    contact,
    generatedAt: buildGeneratedAt,
    paaItems,
  });

  const titleTag = `2026 ${state.name} Elections & Voter Guide | CivixThread`;
  const ogTitle = `2026 ${state.name} Elections, Voter Registration & Ballot Guide`;
  const description = `Find 2026 ${state.name} election dates, voter registration rules, ballot measures, candidates, and the official ${state.name} election office. Free.`;

  return (
    <Box component="main" sx={{ backgroundColor: "#ffffff", minHeight: "100vh" }}>
      <Helmet>
        <title>{titleTag}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE_URL}/elections/${state.slug}`} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_US" />
        <meta
          property="og:url"
          content={`${SITE_URL}/elections/${state.slug}`}
        />
        <meta property="og:image" content={`${SITE_URL}/og-elections-2026.png`} />
        <meta property="og:site_name" content="CivixThread" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content={`${SITE_URL}/og-elections-2026.png`}
        />
        <meta name="theme-color" content={PRIMARY} />
        <link
          rel="alternate"
          hrefLang="en-US"
          href={`${SITE_URL}/elections/${state.slug}`}
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
          color: "#ffffff",
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
        }}
      >
        <Container maxWidth="md">
          <Stack
            direction="row"
            spacing={1}
            sx={{
              fontSize: "0.85rem",
              color: "rgba(255,255,255,0.9)",
              mb: 1.5,
              alignItems: "center",
            }}
          >
            <RouterLink
              to="/"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <HomeIcon
                sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
              />
              Home
            </RouterLink>
            <span aria-hidden="true">›</span>
            <RouterLink
              to="/elections"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Elections
            </RouterLink>
            <span aria-hidden="true">›</span>
            <span style={{ color: "#ffffff", fontWeight: 600 }}>
              {state.name}
            </span>
          </Stack>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.6rem" },
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            2026 {state.name} Elections, Voter Registration &amp; Ballot Guide
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box component="article">
          <Typography
            id="state-lead-paragraph"
            sx={{
              fontSize: { xs: "1rem", md: "1.05rem" },
              color: TEXT_PRIMARY,
              lineHeight: 1.65,
              mb: 3,
            }}
          >
            {lead}
          </Typography>

          {/* Key Facts — highest-leverage GEO surface. AI Overviews and
              Perplexity lift this block verbatim because it is structured,
              date-stamped, and factually dense. */}
          <KeyFactsCard
            stateName={state.name}
            generatedAt={buildGeneratedAt}
            facts={keyFacts}
          />

          {/* Compact CTA card — kept text-light so LLMs lift the lead above */}
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${PRIMARY}33`,
              backgroundColor: "#f0fdf4",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              alignItems: { xs: "stretch", sm: "center" },
              mb: 4,
            }}
          >
            <HowToVoteIcon sx={{ color: PRIMARY_DARK, fontSize: 28 }} />
            <Typography
              sx={{ flex: 1, color: TEXT_PRIMARY, fontWeight: 600 }}
            >
              Save {state.name} elections to your personal civic dashboard.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                component={RouterLink}
                to="/auth/signup"
                variant="contained"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  backgroundColor: PRIMARY,
                  minHeight: 44,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: PRIMARY_DARK,
                    boxShadow: "none",
                  },
                }}
              >
                Create account
              </Button>
              <Button
                component="a"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  borderColor: PRIMARY,
                  color: PRIMARY_DARK,
                  minHeight: 44,
                  "&:hover": {
                    borderColor: PRIMARY_DARK,
                    backgroundColor: "#dcfce7",
                  },
                }}
              >
                iOS app
              </Button>
            </Stack>
          </Box>

          <SectionHeading id="upcoming-elections">
            Upcoming elections in {state.name}
          </SectionHeading>

          {/* Compact, scannable date table — second-most-quoted GEO format
              after the Key Facts block. Sits above the rich election cards. */}
          <ElectionDatesTable stateName={state.name} elections={elections} />

          <Box sx={{ mt: 2 }}>
            {elections.length > 0 ? (
              <DemocracyWorksElections
                elections={elections}
                hideReportFeedback
                showBallotData
              />
            ) : (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  {hasFetchedRuntime
                    ? `No upcoming elections published yet for ${state.name}. Check back soon. Election data refreshes daily.`
                    : `Loading upcoming ${state.name} elections…`}
                </Typography>
              </Box>
            )}
          </Box>

          <SectionHeading>
            How do I register to vote in {state.name}?
          </SectionHeading>
          <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6, mb: 1 }}>
            {registrationIntro}
          </Typography>
          {contact?.website && (
            <Button
              component="a"
              href={contact.website}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                borderColor: PRIMARY,
                color: PRIMARY_DARK,
                minHeight: 44,
                mt: 1,
                "&:hover": {
                  borderColor: PRIMARY_DARK,
                  backgroundColor: "#f0fdf4",
                },
              }}
            >
              Register on the official {state.name} site →
            </Button>
          )}

          {paaItems.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <PeopleAlsoAskBlock items={paaItems} />
            </Box>
          )}

          {faqs.length > 0 && (
            <>
              <SectionHeading>
                Common questions about voting in {state.name}
              </SectionHeading>
              <FaqAccordion items={faqs} />
            </>
          )}

          {contact && (
            <>
              <SectionHeading>
                Contact your {state.name} election office
              </SectionHeading>
              <ContactCard stateName={state.name} contact={contact} />
            </>
          )}

          {related.length > 0 && (
            <>
              <SectionHeading id="related-states">
                Election guides for nearby states
              </SectionHeading>
              <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6, mb: 1.5 }}>
                Browse 2026 voter guides and ballot information for other states
                in your region.
              </Typography>
              <Box
                component="nav"
                aria-label={`Related election guides near ${state.name}`}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {related.map((s) => (
                  <RouterLink
                    key={s.code}
                    to={`/elections/${s.slug}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.875,
                        borderRadius: 2,
                        border: `1px solid ${PRIMARY}55`,
                        backgroundColor: "#f0fdf4",
                        color: PRIMARY_DARK,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        transition: "background-color 120ms ease",
                        "&:hover": {
                          backgroundColor: "#dcfce7",
                        },
                      }}
                    >
                      2026 {s.name} elections →
                    </Box>
                  </RouterLink>
                ))}
              </Box>
            </>
          )}

          {/* Conversion band */}
          <Box
            sx={{
              mt: 6,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
              color: "#ffffff",
            }}
          >
            <Typography
              component="h2"
              sx={{ fontSize: "1.4rem", fontWeight: 800, mb: 1 }}
            >
              Stay civically engaged in {state.name} year-round.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.95)", mb: 2.5 }}>
              Save elections, get deadline reminders, and follow your{" "}
              {state.name} federal and state representatives, all in one civic
              dashboard.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                component={RouterLink}
                to="/auth/signup"
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  background: "#ffffff",
                  backgroundColor: "#ffffff",
                  color: PRIMARY_DARK,
                  minHeight: 44,
                  boxShadow: "none",
                  "&.MuiButton-containedPrimary": {
                    background: "#ffffff",
                    backgroundColor: "#ffffff",
                    color: PRIMARY_DARK,
                  },
                  "&:hover": {
                    background: "#f1f5f9",
                    backgroundColor: "#f1f5f9",
                    boxShadow: "none",
                    color: PRIMARY_DARK,
                    opacity: 1,
                  },
                }}
              >
                Create your free account
              </Button>
              <Button
                component="a"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  borderColor: "#ffffff",
                  color: "#ffffff",
                  minHeight: 44,
                  "&:hover": {
                    borderColor: "#ffffff",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Download iOS app
              </Button>
            </Stack>
          </Box>

        </Box>
      </Container>
    </Box>
  );
}
